import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Container } from '../layout/Container';
import Button from '../components/Button';
import VideoPlayer from '../components/VideoPlayer';
import ENV_CONFIG from '../config/environment-unified';
import { useSelector } from 'react-redux';
import useTranslation from '../hooks/useTranslation';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const EventWatch = () => {
  const { slug } = useParams();
  const history = useHistory();
  const location = useLocation();
  const query = useQuery();
  const pov = query.get('pov'); // can be username or channel id
  const { t } = useTranslation();

  const user = useSelector((s) => s.user?.data);
  const isLoggedIn = useSelector((s) => s.user?.isLoggedIn);

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [streams, setStreams] = useState([]);
  const [selected, setSelected] = useState(null);
  const [hasAccess, setHasAccess] = useState(true);
  const [checkingAccess, setCheckingAccess] = useState(false);
  const [gettingAccess, setGettingAccess] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [walletBalance, setWalletBalance] = useState(null);

  const redirectToGcoinStore = (balance, price) => {
    const returnTo = `${location.pathname}${location.search}`;
    try {
      localStorage.setItem('postGcoinPurchaseRedirect', returnTo);
    } catch (_) {
      // ignore storage errors (private mode, etc.)
    }
    const params = new URLSearchParams({
      redirect: returnTo,
      reason: 'insufficient_balance',
      balance: String(balance ?? ''),
      price: String(price ?? ''),
    });
    history.push(`/gcoin-store?${params.toString()}`);
  };

  const isInsufficientBalanceError = (err) => {
    const status = err?.response?.status;
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.response?.data?.msg ||
      err?.message ||
      '';
    const norm = String(msg).toLowerCase();
    return (
      status === 402 ||
      status === 403 ||
      norm.includes('solde insuffisant') ||
      (norm.includes('insuff') && norm.includes('solde')) ||
      norm.includes('not enough') ||
      (norm.includes('g') && norm.includes('coin') && norm.includes('insuff'))
    );
  };

  const getEffectiveTicketPrice = (ev) => {
    if (!ev) return 0;
    const cfg = ev.ticketConfig || ev.ticket_config;
    const cfgPrice =
      cfg && typeof cfg === "object"
        ? (cfg.price ?? cfg.ticketPrice ?? cfg.amount)
        : undefined;

    const raw =
      cfgPrice !== undefined && cfgPrice !== null && cfgPrice !== ""
        ? cfgPrice
        : ev.ticketPrice;

    const n = typeof raw === "string" ? parseFloat(raw) : Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  const hasValidTicketFromTicketService = async (userId, eventId) => {
    try {
      const res = await axios.get(`${ENV_CONFIG.TICKET_USER_ENDPOINT}/${userId}`, {
        withCredentials: true,
        timeout: 6000,
      });
      const tickets = res.data?.tickets || res.data?.data?.tickets || [];
      if (!Array.isArray(tickets) || tickets.length === 0) return false;

      return tickets.some((t) => {
        const tEventId = t?.event_id ?? t?.eventId ?? t?.eventID;
        if (String(tEventId) !== String(eventId)) return false;

        // ticket service uses snake_case `is_validated`
        if (t?.is_validated === true || t?.isValidated === true) return false;

        return true;
      });
    } catch (_) {
      return false;
    }
  };

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        setLoading(true);

        const eventRes = await axios.get(`${ENV_CONFIG.EVENTS_ENDPOINT}/events/${slug}`, {
          withCredentials: true
        });
        const ev = eventRes.data?.event;
        if (!mounted) return;
        setEvent(ev);

        const streamRes = await axios.get(`/api/stream/streams/by-event/${ev.id}`, { withCredentials: true });
        const list = streamRes.data?.streams || [];
        if (!mounted) return;
        setStreams(list);

        // pick selected POV (query param first, then first stream)
        const preferred = pov
          ? list.find(s => String(s.id) === String(pov) || String(s.username) === String(pov))
          : null;
        setSelected(preferred || list[0] || null);

        // Access check for paid events (supports legacy events where price lives in ticketConfig.price)
        const priceNum = getEffectiveTicketPrice(ev);

        if (priceNum <= 0) {
          setHasAccess(true);
          return;
        }

        if (!isLoggedIn || !user?.id) {
          setHasAccess(false);
          return;
        }

        setCheckingAccess(true);
        try {
          // Organizer can always watch
          if (String(ev.createdBy) === String(user.id)) {
            setHasAccess(true);
            return;
          }

          // Tournament players/streamers are tracked via eventStreamer, not participants tickets.
          // If the user is an active assignee for this tournament event, grant access.
          if (ev?.category?.slug === 'tournament') {
            try {
              const playersRes = await axios.get(`${ENV_CONFIG.EVENTS_ENDPOINT}/events/${slug}/players`, {
                withCredentials: true
              });
              const list = playersRes.data?.players || [];
              const isAssignee = list.some((p) => String(p?.user?.id) === String(user.id));
              if (isAssignee) {
                setHasAccess(true);
                return;
              }
            } catch (_) {
              // ignore and continue with ticket checks
            }
          }

          // First: event service participant ticketId
          const participantsRes = await axios.get(`${ENV_CONFIG.EVENT_PARTICIPANTS_ENDPOINT}/${ev.id}`, { withCredentials: true });
          const participants = participantsRes.data?.participants || [];
          const me = participants.find((p) => String(p.userId) === String(user.id));
          const hasTicketId = !!me?.ticketId;
          if (hasTicketId) {
            setHasAccess(true);
            return;
          }

          // Fallback: ticket service record (for legacy flows where participant.ticketId isn't set)
          const hasTicket = await hasValidTicketFromTicketService(user.id, ev.id);
          setHasAccess(!!hasTicket);
        } catch (e) {
          setHasAccess(false);
        } finally {
          setCheckingAccess(false);
        }
      } catch (e) {
        console.error('❌ Failed to load event watch page:', e);
        toast.error(t('eventWatch.failedToLoad', 'Impossible de charger l’événement'));
        history.push('/events');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, isLoggedIn, user?.id]);

  const handleGetAccess = async () => {
    if (!event) return;
    const isPastEvent = !!event?.endDate && new Date(event.endDate).getTime() < Date.now();
    if (isPastEvent) {
      toast.error(t('eventdetails.event_ended', 'Event Ended'));
      return;
    }
    const priceNum = getEffectiveTicketPrice(event);
    if (priceNum <= 0) return;

    if (!isLoggedIn || !user?.id) {
      history.push('/?noAuth');
      return;
    }

    try {
      setGettingAccess(true);
      const balRes = await axios.get(`${ENV_CONFIG.WALLET_BALANCE_ENDPOINT}/${user.id}`, { withCredentials: true, timeout: 6000 });
      const balance = parseFloat(balRes.data?.balance) || 0;
      setWalletBalance(balance);
      if (balance < priceNum) {
        toast.error(t('eventWatch.insufficientBalanceRedirect', 'Solde insuffisant. Redirection vers l’achat de G‑Coins…'));
        redirectToGcoinStore(balance, priceNum);
        return;
      }

      await axios.post(`${ENV_CONFIG.EVENT_PARTICIPANTS_ENDPOINT}/${event.id}`, {}, { withCredentials: true });
      setHasAccess(true);
      toast.success(t('eventWatch.accessGranted', 'Accès accordé !'));
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.message || e?.response?.data?.error || e.message;
      if (status === 401) {
        history.push('/?noAuth');
        return;
      }
      if (isInsufficientBalanceError(e)) {
        toast.error(t('eventWatch.insufficientBalanceRedirect', 'Solde insuffisant. Redirection vers l’achat de G‑Coins…'));
        redirectToGcoinStore(walletBalance, priceNum);
        return;
      }
      toast.error(msg || t('eventWatch.failedToGetAccess', 'Impossible d’obtenir l’accès'));
    } finally {
      setGettingAccess(false);
    }
  };

  const selectPov = (s) => {
    setSelected(s);
    // keep shareable POV without navigation
    history.replace(`/events/${slug}/watch?pov=${encodeURIComponent(s.username || s.id)}`);
  };

  useEffect(() => {
    if (!hasAccess || !selected?.channelArn || !selected?.playbackLink) return;

    const channelArn = selected.channelArn;
    const baseUrl = ENV_CONFIG.API_BASE_URL || '';
    let heartbeatId;

    const join = async () => {
      try {
        await axios.post(`${baseUrl}/api/stream/viewers/join`, { channelArn }, { withCredentials: true });
        window.dispatchEvent(new CustomEvent('corearena:viewers:refresh'));
      } catch (_) {
      }
    };

    const heartbeat = async () => {
      try {
        await axios.post(`${baseUrl}/api/stream/viewers/heartbeat`, { channelArn }, { withCredentials: true });
      } catch (_) {
      }
    };

    const leave = async () => {
      try {
        await axios.post(`${baseUrl}/api/stream/viewers/leave`, { channelArn }, { withCredentials: true });
        window.dispatchEvent(new CustomEvent('corearena:viewers:refresh'));
      } catch (_) {
      }
    };

    join();
    heartbeatId = setInterval(heartbeat, 25000);

    const onUnload = () => {
      leave();
    };

    window.addEventListener('beforeunload', onUnload);

    return () => {
      clearInterval(heartbeatId);
      window.removeEventListener('beforeunload', onUnload);
      leave();
    };
  }, [hasAccess, selected?.channelArn, selected?.playbackLink]);

  if (loading) {
    return (
      <Container style={{ paddingTop: '4rem', paddingBottom: '2rem' }}>
        <h2>{t('common.loading', 'Chargement...')}</h2>
      </Container>
    );
  }

  if (!event) {
    return (
      <Container style={{ paddingTop: '4rem', paddingBottom: '2rem' }}>
        <h2>{t('eventWatch.eventNotFound', 'Événement introuvable')}</h2>
      </Container>
    );
  }

  return (
    <Container style={{ paddingTop: '4rem', paddingBottom: '2rem' }}>
      <HeaderRow>
        <div>
          <Title>{event.name}</Title>
          <Meta>
            <span>{event.category?.icon} {event.category?.name}</span>
            <Dot>•</Dot>
            <Link to={`/events/${event.slug}`}>{t('eventWatch.eventPage', 'Page de l’événement')}</Link>
          </Meta>
        </div>
        <Actions>
          <Link to={`/events/${event.slug}`}>
            <Button variant="secondary" size="small">{t('eventWatch.details', 'Détails')}</Button>
          </Link>
          {event.category?.slug === 'tournament' && (
            <Link to={`/events/${event.slug}`}>
              <Button variant="secondary" size="small">{t('eventWatch.bracket', 'Bracket')}</Button>
            </Link>
          )}
        </Actions>
      </HeaderRow>

      <Card>
        {!hasAccess && (
          <AccessBlock>
            <h3 style={{ marginTop: 0 }}>{t('eventWatch.premiumContent', 'Contenu premium')}</h3>
            <p style={{ color: '#9CA3AF', marginTop: 8 }}>
              {t('eventWatch.needAccess', 'Vous devez avoir un accès pour regarder cet événement.')}
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
              <Button
                variant="primary"
                size="small"
                disabled={gettingAccess || checkingAccess || (!!event?.endDate && new Date(event.endDate).getTime() < Date.now())}
                onClick={handleGetAccess}
              >
                {gettingAccess
                  ? t('eventWatch.processing', 'Traitement...')
                  : `${t('eventWatch.getAccess', 'Obtenir l’accès')} – ${event.ticketPrice} ${t('common.gcoins', 'G-Coins')}`
                }
              </Button>
              <Link to={`/events/${event.slug}`}>
                <Button variant="secondary" size="small">{t('eventWatch.details', 'Détails')}</Button>
              </Link>
            </div>
          </AccessBlock>
        )}

        {hasAccess && (
          <PovRow>
            <PovLabel>{t('eventWatch.livePov', 'POV en direct')}</PovLabel>
            <PovList>
              {streams.length === 0 ? (
                <EmptyPov>{t('eventWatch.noLivePovYet', 'Aucun POV en direct pour le moment.')}</EmptyPov>
              ) : (
                streams.map((s) => (
                  <PovButton
                    key={s.id}
                    active={selected?.id === s.id}
                    onClick={() => selectPov(s)}
                    title={s.title || s.username}
                  >
                    <AvatarImg
                      src={s.avatar || 'https://via.placeholder.com/24'}
                      alt={s.username}
                    />
                    <span>{s.username}</span>
                  </PovButton>
                ))
              )}
            </PovList>
          </PovRow>
        )}

        {hasAccess && selected?.playbackLink ? (
          <PlayerWrap>
            <VideoPlayer
              key={selected.playbackLink}
              type="hls"
              src={selected.playbackLink}
              isLive={true}
              isMute={false}
              autoPlay={true}
              id="event-watch-player"
            />
          </PlayerWrap>
        ) : hasAccess ? (
          <NoPlayback>
            {t('eventWatch.noPlaybackYet', 'Aucun playback live disponible pour le moment.')}
          </NoPlayback>
        ) : null}

        {hasAccess && selected?.username && (
          <FooterRow>
            <Link to={`/stream/${selected.username}`}>
              <Button variant="secondary" size="small">{t('eventWatch.openPovPage', 'Ouvrir la page stream')}</Button>
            </Link>
          </FooterRow>
        )}
      </Card>

      {showBalanceModal && (
        <ModalOverlay onClick={() => setShowBalanceModal(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>{t('eventWatch.notEnoughGcoins', 'Pas assez de G‑Coins')}</h3>
            <p style={{ color: '#9CA3AF' }}>
              {t('eventWatch.balance', 'Solde')}: <strong>{walletBalance ?? 0}</strong> • {t('eventWatch.price', 'Prix')}: <strong>{event.ticketPrice}</strong>
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
              <Button variant="secondary" size="small" onClick={() => setShowBalanceModal(false)}>{t('common.cancel', 'Annuler')}</Button>
              <Button variant="primary" size="small" onClick={() => history.push('/gcoin-store')}>{t('eventWatch.buyGcoins', 'Acheter des G‑Coins')}</Button>
            </div>
          </ModalCard>
        </ModalOverlay>
      )}
    </Container>
  );
};

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.color.darkerText};
`;

const Meta = styled.div`
  margin-top: 6px;
  color: ${({ theme }) => theme.color.lighterText};
  display: flex;
  align-items: center;
  gap: 8px;
  a { color: ${({ theme }) => theme.color.primary}; }
`;

const Dot = styled.span`
  opacity: 0.6;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const Card = styled.div`
  border-radius: 12px;
  border: 1px solid #2D3748;
  background: #0b1220;
  padding: 16px;
`;

const PovRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

const PovLabel = styled.div`
  color: ${({ theme }) => (theme.mode === 'dark' ? '#E5E7EB' : theme.color.darkerText)};
  font-weight: 700;
`;

const PovList = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const EmptyPov = styled.div`
  color: #9CA3AF;
  font-size: 14px;
`;

const PovButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid ${({ active, theme }) =>
    active ? theme.color.primary : (theme.mode === 'dark' ? '#2D3748' : theme.color.border)};
  background: ${({ active, theme }) =>
    active
      ? (theme.mode === 'dark' ? 'rgba(102,126,234,0.15)' : 'rgba(69,105,255,0.12)')
      : (theme.mode === 'dark' ? '#0b1220' : theme.color.secondaryBg)};
  color: ${({ theme }) => (theme.mode === 'dark' ? '#E5E7EB' : theme.color.darkerText)};
  cursor: pointer;
`;

const AvatarImg = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 999px;
  object-fit: cover;
`;

const PlayerWrap = styled.div`
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #2D3748;
  background: #000;
`;

const FooterRow = styled.div`
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
`;

const NoPlayback = styled.div`
  color: #9CA3AF;
  font-size: 14px;
  padding: 20px 0;
`;

const AccessBlock = styled.div`
  border: 1px solid #2D3748;
  background: rgba(0,0,0,0.35);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 9999;
`;

const ModalCard = styled.div`
  width: 100%;
  max-width: 520px;
  border-radius: 12px;
  border: 1px solid #2D3748;
  background: #0b1220;
  padding: 16px;
`;

export default EventWatch;



